import { useState } from 'react';
import { notificationsAPI } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { MessageSquare, Copy, ExternalLink, Send, Check, Phone } from 'lucide-react';

const TRIGGER_LABELS = {
  order_confirmed: 'Order Confirmed', fabric_ordered: 'Fabric Ordered',
  fabric_received: 'Fabric Received', production_started: 'Production Started',
  ready_for_trial: 'Ready for Trial', alterations_complete: 'Alterations Complete',
  ready_for_delivery: 'Ready for Delivery', delivered: 'Delivered',
  payment_received: 'Payment Received', delay_alert: 'Delay Alert',
};

export default function WhatsAppPromptDialog({ notification, onClose }) {
  const [copied, setCopied] = useState(false);
  const [messageText, setMessageText] = useState(notification?.message_text || '');
  const [marking, setMarking] = useState(false);

  if (!notification) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(messageText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Message copied');
    } catch { toast.error('Copy failed'); }
  };

  const handleOpenWhatsApp = () => {
    if (notification.whatsapp_link) {
      window.open(notification.whatsapp_link, '_blank');
    } else if (notification.client_phone) {
      const encoded = encodeURIComponent(messageText);
      const phone = notification.client_phone.replace(/[^\d]/g, '');
      window.open(`https://wa.me/${phone}?text=${encoded}`, '_blank');
    }
  };

  const handleMarkSent = async () => {
    setMarking(true);
    try {
      await notificationsAPI.updateLogStatus(notification.id, 'sent');
      toast.success('Marked as sent');
      onClose();
    } catch { toast.error('Failed'); }
    setMarking(false);
  };

  const handleSkip = async () => {
    onClose();
  };

  return (
    <Dialog open={true} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-md" data-testid="whatsapp-prompt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: '#25D366' }}>
              <MessageSquare className="w-3.5 h-3.5 text-white" />
            </div>
            Send WhatsApp to Client
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]" style={{ backgroundColor: 'rgba(37,211,102,0.08)', color: '#128C7E' }}>
              {TRIGGER_LABELS[notification.trigger_event] || notification.trigger_event}
            </Badge>
            {notification.client_phone && (
              <span className="text-xs flex items-center gap-1" style={{ color: 'var(--ink-muted)' }}>
                <Phone className="w-3 h-3" /> {notification.client_phone}
              </span>
            )}
          </div>

          <Textarea value={messageText} onChange={e => setMessageText(e.target.value)}
            className="text-sm h-32" data-testid="wa-prompt-message" />

          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 gap-1.5 text-xs h-9" onClick={handleCopy}
              data-testid="wa-copy-btn">
              {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied!' : 'Copy Message'}
            </Button>
            <Button className="flex-1 gap-1.5 text-xs h-9"
              style={{ backgroundColor: '#25D366' }}
              onClick={handleOpenWhatsApp}
              data-testid="wa-open-btn">
              <ExternalLink className="w-3.5 h-3.5" /> Open WhatsApp
            </Button>
          </div>
        </div>

        <DialogFooter className="flex-row gap-2 sm:justify-between">
          <Button variant="ghost" size="sm" className="text-xs" onClick={handleSkip} data-testid="wa-skip-btn">
            Skip for now
          </Button>
          <Button size="sm" className="gap-1 text-xs" style={{ backgroundColor: 'var(--brand-navy)' }}
            onClick={handleMarkSent} disabled={marking} data-testid="wa-mark-sent-btn">
            <Send className="w-3 h-3" /> {marking ? 'Saving...' : 'I Sent It'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
